import React from 'react';

/**
 * All Indexes → Performance Summary, Model: Diff (Apal 2026-11-02: "what is
 * the difference between published model versus the latest one … what you do
 * actually on the dashboard page" — "even if it's same, you have to show both
 * data … clearly show this is the published data and this is the latest
 * data" — "what was published versus a new model, is it bad or not? … they
 * can clearly understand if there's a big problem or not").
 *
 * Every index shows its Published row, then its Latest row, from the server's
 * diff mode: the same official prices, the same last day, the page's
 * execution — so only the model differs.
 *
 * Colour answers "is the new model worse?":
 * - the six figures Today's Health judges, past ITS limits (the same scaled
 *   thresholds as reports/views.py IMPACT_RULES): a filled red / green cell;
 * - any smaller move, or another figure with a clear good direction: red /
 *   green text;
 * - a figure that is neither good nor bad (trades a year, hold): bold white.
 * The verdict beside the Latest tag uses the dashboard's rule: worse on a
 * judged figure, or a non-negotiable newly broken.
 */

// dir: +1 higher is better, -1 lower is better. floor/rel: the limit for the
// six judged figures — mirrors IMPACT_RULES on the server
const RULES = {
  strategy_cagr: { dir: 1, floor: 1.0, rel: 5, label: 'CAGR', judged: true },
  win_rate: { dir: 1, floor: 2.0, rel: null, label: 'win rate', judged: true },
  worst_loss: { dir: 1, floor: 0.5, rel: 10, label: 'worst loss', judged: true },
  worst_cons_loss: { dir: 1, floor: 1.0, rel: 10, label: 'cons. losses', judged: true },
  avg_return: { dir: 1, floor: 0.5, rel: 10, label: 'avg return', judged: true },
  exp_ratio: { dir: 1, floor: 0.1, rel: 10, label: 'exp. ratio', judged: true },
  time_in_market_pct: { dir: -1 },
  win_loss_ratio: { dir: 1 },
  best_gain: { dir: 1 },
  avg_mae: { dir: -1 },
};

/** worse / better (past the limit), worse_minor / better_minor, moved (no good
 *  direction), same. */
export function callFor(key, pub, lat) {
  if (pub === lat) return 'same';
  const rule = RULES[key];
  if (!rule || pub == null || lat == null) return 'moved';
  const gain = (lat - pub) * rule.dir;       // > 0: Latest is better here
  if (rule.judged) {
    const thr = rule.rel ? Math.max(rule.floor, Math.abs(pub) * rule.rel / 100) : rule.floor;
    if (gain < -thr) return 'worse';
    if (gain > thr) return 'better';
  }
  return gain < 0 ? 'worse_minor' : gain > 0 ? 'better_minor' : 'same';
}

const pct = (v) => (v == null ? '—' : `${v}%`);
const pct2 = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}%`);

export const DIFF_COLUMNS = [
  { key: 'since', label: 'Since', align: 'center', fmt: (v) => v ?? '—' },
  { key: 'index_cagr', label: 'Index', fmt: pct2 },
  { key: 'strategy_cagr', label: 'MV 1X', fmt: pct2 },
  { key: 'trades_per_year', label: 'Trades/Yr', align: 'center', fmt: (v) => v ?? '—' },
  { key: 'avg_days_held', label: 'Avg Hold', align: 'center', fmt: (v) => (v == null ? '—' : `${v}d`) },
  { key: 'time_in_market_pct', label: '% In Mkt', fmt: pct },
  { key: 'win_rate', label: 'Win Rate', fmt: pct },
  { key: 'avg_return', label: 'Avg Ret', fmt: pct },
  { key: 'win_loss_ratio', label: 'Gain/Loss', align: 'center', fmt: (v) => (v == null ? '—' : `${v}x`) },
  { key: 'exp_ratio', label: 'Exp. Ratio', align: 'center', fmt: (v) => (v == null ? '—' : Number(v).toFixed(2)) },
  { key: 'best_gain', label: 'Best', fmt: pct },
  { key: 'worst_loss', label: 'Worst', fmt: pct },
  { key: 'worst_cons_loss', label: 'Worst Streak', fmt: pct },
  { key: 'avg_mae', label: 'MAE', fmt: pct },
];

/** Per index: every figure's call, and the verdict an analyst reads first. */
/** The live model changed since MV's last Latest push — the server's
 *  latest_is_behind rule (a different number, never compared by size: MV
 *  renumbers). Then nothing is judged: both rows show, uncoloured. */
export const latestBehind = (pub, lat) =>
  lat?.model_revision != null && pub?.published_revision != null && lat.model_revision !== pub.published_revision;

export function diffIndex(pub, lat) {
  const cells = {};
  let changed = 0;
  const behind = latestBehind(pub, lat);
  for (const c of DIFF_COLUMNS) {
    const a = pub?.[c.key];
    const b = lat?.[c.key];
    const call = behind ? (a === b ? 'same' : 'unjudged') : callFor(c.key, a, b);
    if (call !== 'same') changed += 1;
    cells[c.key] = { pub: a, lat: b, call };
  }
  if (behind) {
    return { cells, changed, worse: [], better: [], breaks: [], verdict: 'behind', live: pub.published_revision };
  }
  const worse = DIFF_COLUMNS.filter(c => cells[c.key].call === 'worse').map(c => RULES[c.key].label);
  const better = DIFF_COLUMNS.filter(c => cells[c.key].call === 'better').map(c => RULES[c.key].label);
  const was = new Set(pub?.non_negotiables || []);
  // the dashboard's rule: newly broken = clearly past the bar (server margin)
  const breaks = (lat?.non_negotiables_clear || lat?.non_negotiables || []).filter(n => !was.has(n));
  const verdict = worse.length || breaks.length ? 'worse' : better.length ? 'better' : changed ? 'small' : 'same';
  return { cells, changed, worse, better, breaks, verdict };
}

const CELL_CLASS = {
  worse: 'bg-red-900/30 text-red-400 font-semibold',
  better: 'bg-green-900/30 text-green-400 font-semibold',
  worse_minor: 'text-red-400',
  better_minor: 'text-green-400',
  moved: 'text-white font-semibold',
  unjudged: 'text-dark-300',
  same: 'text-dark-300',
};
const alignClass = (c) => (c.align === 'center' ? 'text-center' : 'text-right');
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// the Model control's own colours: Published sky, Latest fuchsia
const Tag = ({ latest, children }) => (
  <span className={`inline-block px-1.5 py-px rounded text-[10px] font-semibold uppercase tracking-wide text-white ${latest ? 'bg-fuchsia-600' : 'bg-sky-600'}`}>
    {children}
  </span>
);

export function Verdict({ d }) {
  if (d.verdict === 'worse') {
    const what = [...d.worse.map(cap), ...d.breaks.map(n => `breaks ${n}`)].join(', ');
    return <span data-verdict="worse" className="text-[11px] font-semibold text-red-400" title={what}>⚠ Worse: {what}</span>;
  }
  if (d.verdict === 'better') {
    return <span data-verdict="better" className="text-[11px] font-semibold text-green-400">▲ Better: {d.better.map(cap).join(', ')}</span>;
  }
  if (d.verdict === 'behind') {
    return (
      <span data-verdict="behind" className="text-[11px] text-dark-300"
        title={`The live model is now v${d.live}; this Latest was computed before it. Judging it would grade the old model — nothing is judged until MarketVibes pushes a fresh Latest.`}>
        Out of date · live is v{d.live}
      </span>
    );
  }
  if (d.verdict === 'small') return <span data-verdict="small" className="text-[11px] text-dark-300">Small changes</span>;
  return <span data-verdict="same" className="text-[11px] text-dark-400">✓ Same</span>;
}

function IndexRows({ label, version, d, hasLatest }) {
  return (
    <>
      <tr className="border-t border-dark-600" data-diff={d.verdict} data-model="published">
        <td className="pt-3 pb-1.5 px-2">
          <div className="text-white font-medium leading-tight">{label}</div>
          <div className="mt-1"><Tag>Published</Tag></div>
        </td>
        {DIFF_COLUMNS.map(c => (
          <td key={c.key} className={`pt-3 pb-1.5 px-2 align-bottom text-dark-300 tabular-nums ${alignClass(c)}`}>{c.fmt(d.cells[c.key].pub)}</td>
        ))}
      </tr>
      {hasLatest ? (
        <tr className="bg-fuchsia-500/[0.05]" data-model="latest">
          <td className="pt-1.5 pb-3 px-2">
            <Tag latest>Latest{version != null && <span className="normal-case"> v{version}</span>}</Tag>
            <div className="mt-1 max-w-[190px] leading-tight"><Verdict d={d} /></div>
          </td>
          {DIFF_COLUMNS.map(c => {
            const cell = d.cells[c.key];
            return (
              <td key={c.key} data-call={cell.call}
                className={`pt-1.5 pb-3 px-2 tabular-nums rounded ${alignClass(c)} ${CELL_CLASS[cell.call]}`}>
                {c.fmt(cell.lat)}
              </td>
            );
          })}
        </tr>
      ) : (
        <tr className="bg-fuchsia-500/[0.05]" data-model="latest">
          <td className="pt-1.5 pb-3 px-2 whitespace-nowrap"><Tag latest>Latest</Tag></td>
          <td colSpan={DIFF_COLUMNS.length} className="pt-1.5 pb-3 px-2 text-center text-dark-400 text-xs italic">
            No Latest data for this index yet — MarketVibes hasn't pushed the current model
          </td>
        </tr>
      )}
    </>
  );
}

export default function ModelDiffTable({ published, latest, regionExtra }) {
  const latByCode = {};
  (latest || []).forEach(r => r.indexes.forEach(i => { latByCode[i.symbol] = i; }));
  const rows = (published || []).flatMap(r => r.indexes.filter(i => i.has_shorts_data !== false && latByCode[i.symbol]))
    .map(i => ({ idx: i, d: diffIndex(i, latByCode[i.symbol]) }));
  const worse = rows.filter(r => r.d.verdict === 'worse');
  const behind = rows.filter(r => r.d.verdict === 'behind');
  const judged = rows.length - behind.length;
  const nChanged = rows.filter(r => r.d.changed && r.d.verdict !== 'behind').length;
  return (
    <div data-testid="model-diff">
      <div className="mb-5 px-4 py-3 rounded-lg bg-dark-800/60 border border-dark-600 space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-white">
            <span className="px-2 py-0.5 rounded bg-sky-600 text-white text-[11px] uppercase tracking-wide">Published</span>
            <span className="text-dark-400">vs</span>
            <span className="px-2 py-0.5 rounded bg-fuchsia-600 text-white text-[11px] uppercase tracking-wide">Latest</span>
          </div>
          {/* the analyst's question first: is the new model worse anywhere? */}
          <span className="text-sm" data-testid="model-diff-summary">
            {worse.length > 0 ? (
              <span className="text-red-400 font-semibold">
                ⚠ Worse with the Latest model in {worse.length} of {judged}: {worse.map(r => `${r.idx.label} (${[...r.d.worse, ...r.d.breaks.map(n => `breaks ${n}`)].join(', ')})`).join(' · ')}
              </span>
            ) : judged > 0 ? (
              <span className="text-green-400 font-semibold">✓ No index is worse with the Latest model</span>
            ) : null}
            {judged > 0 && <span className="text-dark-400"> · {nChanged ? `${nChanged} of ${judged} changed at all` : 'nothing changed'}</span>}
            {behind.length > 0 && (
              <span className="text-dark-300" data-testid="model-diff-behind">
                {judged > 0 ? ' · ' : ''}{behind.length} not judged — their Latest predates the live model ({behind.map(r => r.idx.label).join(', ')})
              </span>
            )}
          </span>
          <span className="text-xs text-dark-400 sm:ml-auto">
            Same prices, same last day — only the model differs
          </span>
        </div>
        <div className="text-[11px] text-dark-400">
          Each index: its Published row, then its Latest row. In Latest:{' '}
          <span className="px-1 rounded bg-red-900/30 text-red-400 font-semibold">worse</span> /{' '}
          <span className="px-1 rounded bg-green-900/30 text-green-400 font-semibold">better</span> past Today's Health's limits (CAGR, win rate, worst loss, cons. losses, avg return, exp. ratio) ·{' '}
          <span className="text-red-400">red</span> / <span className="text-green-400">green</span> a smaller move that way ·{' '}
          <span className="text-white font-semibold">white</span> moved, neither good nor bad
        </div>
      </div>
      <div className="space-y-8">
        {(published || []).map(region => (
          <div key={region.code}>
            {/* the normal table's region header, From / To years included */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span>{region.region}</span>
                <span className="text-dark-400 font-normal text-sm">({region.indexes.length} indexes)</span>
              </h3>
              {regionExtra && regionExtra(region)}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-dark-400 text-xs uppercase tracking-wider">
                    <th className="text-left py-3 px-2 font-medium">Index</th>
                    {DIFF_COLUMNS.map(c => (
                      <th key={c.key} className={`py-3 px-2 font-medium ${alignClass(c)}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {region.indexes.map(idx => {
                    if (idx.has_shorts_data === false) {
                      // the normal table's own line for an index without short data
                      return (
                        <tr key={idx.symbol} className="border-t border-dark-600 opacity-40">
                          <td className="py-3 px-2 text-dark-400 font-medium">{idx.label}</td>
                          <td colSpan={DIFF_COLUMNS.length} className="py-3 px-2 text-center text-dark-500 text-xs italic">
                            Shorts not available for this index
                          </td>
                        </tr>
                      );
                    }
                    const lat = latByCode[idx.symbol];
                    return <IndexRows key={idx.symbol} label={idx.label} version={lat?.model_revision} d={diffIndex(idx, lat)} hasLatest={!!lat} />;
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
